import { useEffect } from 'react'
import { Grid, Box } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import { InputField, SwitchField, TextareaField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import ReclamacionComunidad from 'src/components/views/EscritosLexnet/components/Monitorio/components/Datos/ReclamacionComunidad'
import ReclamacionConsumidor from 'src/components/views/EscritosLexnet/components/Monitorio/components/Datos/ReclamacionConsumidor'
import MateriaOrDelitoAutocomplete from 'src/components/views/EscritosLexnet/components/shared/components/iniciadores/MateriaOrDelitoAutocomplete'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { TipoCuantiaOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { Orden } from 'src/interfaces/Catalog'

function Datos(): JSX.Element {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<EscritosFormValues>()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const { allowFloatNumber } = useNumberValidations()

  const intervinientes = values[FormField.INTERVINIENTES]

  function showCuantiaFields(): JSX.Element | null {
    return (
      <>
        {orden && orden.value && (
          <>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_PRINCIPAL}
                label={t('form.field.cantidad_reclamada')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_INTERESES_REMUNERATORIOS}
                label={t('form.field.datos_cuantia_intereses_remuneratorios')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_INTERESES_DEMORA}
                label={t('form.field.datos_cuantia_intereses_demora')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_COMISION_IMPAGO_RECIBOS}
                label={t('form.field.datos_cuantia_comision_impago_recibos')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <InputField
                id={FormField.DATOS_CUANTIA_PENALIZACION_RETRASO_PAGO}
                label={t('form.field.datos_cuantia_penalizacion_retraso_pago')}
                clearable
                currencyType
                required
                onChange={allowFloatNumber}
                maxLength={16}
              />
            </Grid>
          </>
        )}
      </>
    )
  }

  function showMonitorioTextsFields(): JSX.Element | null {
    if (!orden || !orden.value) {
      return null
    }
    return (
      <Box sx={{ marginTop: '16px' }}>
        <TextareaField
          id={FormField.DATOS_ORIGEN_DEUDA}
          label={t('form.field.origen_deuda')}
          maxLength={20000}
          required
          disabled={!orden.value}
        />
        <TextareaField
          id={FormField.DATOS_FUNDAMENTOS_DERECHO}
          label={t('form.field.fundamentos_derecho')}
          maxLength={20000}
          disabled={!orden.value}
        />
        <TextareaField
          id={FormField.DATOS_PETICION}
          label={t('form.field.peticion')}
          maxLength={20000}
          required
          disabled={!orden.value}
        />
        <TextareaField
          id={FormField.DATOS_PETICION_COMPLEMENTARIA}
          label={t('form.field.peticiones_complementarias')}
          maxLength={20000}
          disabled={!orden.value}
        />
      </Box>
    )
  }

  function conditionReclamacionConsumidor() {
    return intervinientes?.some(
      interviniente =>
        interviniente.intervencionTipo?.codigo === 'DDO' &&
        interviniente.personaTipo?.codigo === '002'
    )
  }

  function conditionReclamacionComunidad() {
    return intervinientes?.some(
      interviniente =>
        interviniente.intervencionTipo?.codigo === 'SOL' &&
        interviniente.personaTipo?.codigo === '003'
    )
  }

  useEffect(() => {
    if (orden && orden.value && !values[FormField.DATOS_TIPO_CUANTIA]) {
      setFieldValue(FormField.DATOS_TIPO_CUANTIA, {
        codigo: 'D',
        descripcion: TipoCuantiaOpciones.DETERMINADA
      })
    }
    // eslint-disable-next-line
  }, [orden?.value])

  useEffect(() => {
    if (!conditionReclamacionConsumidor()) {
      setFieldValue(FormField.DATOS_RECLAMACION_CONSUMIDOR, false, false)
    }

    if (!conditionReclamacionComunidad()) {
      setFieldValue(FormField.DATOS_RECLAMACION_COMUNIDAD, false, false)
      setFieldValue(FormField.DATOS_DEUDA_PARTIDO_JUDICIAL, false, false)
    }

    // eslint-disable-next-line
  }, [intervinientes])

  return (
    <FieldsetGroup legend={t('form.legend.datos')}>
      <Grid container spacing={2}>
        <MateriaOrDelitoAutocomplete />
        {orden && orden.value && (
          <>
            {conditionReclamacionConsumidor() && <ReclamacionConsumidor />}
            {conditionReclamacionComunidad() && <ReclamacionComunidad />}
          </>
        )}
      </Grid>
      {showMonitorioTextsFields()}
      <Grid container spacing={2}>
        {showCuantiaFields()}
      </Grid>
      <TextareaField
        id={FormField.DATOS_OBSERVACIONES}
        label={t('form.field.observaciones')}
        maxLength={20000}
        disabled={!orden.value}
      />
      <Box display="flex" flexDirection="row" sx={{ marginTop: '16px' }}>
        <SwitchField
          id={FormField.DATOS_MASC}
          label={t('form.field.masc')}
          disabled={!orden.value}
        />
      </Box>
      {orden.value && !values[FormField.DATOS_MASC] && (
        <Box sx={{ marginTop: '16px' }}>
          <InfoBar
            type={AlertType.ERROR}
            message={t('alert.masc_requisito_procedibilidad')}
            iconVariant="info"
            showCloseIcon={false}
          />
        </Box>
      )}
    </FieldsetGroup>
  )
}

export default Datos
